import { Inbox, Plus } from 'lucide-react';
import {
  LinkBase,
  useGetResourceLabel,
  useResourceContext,
  useResourceDefinition,
  useTranslate,
} from 'ra-core';
import { buttonVariants } from '../../components/ui/button';

export type MoonstarListEmptyProps = {
  hasCreate?: boolean;
  /**
   * The link to navigate to when creating a new item. Requires {@see hasCreate} to be set to `true` to render
   */
  createTo: string;
};

/**
 * Renders an "Empty" placeholder with a create button linking to `createTo`. Designed for nested resource lists
 * whose URLs don't follow the default react-admin pattern (the kit `CreateButton` has no `to` prop).
 * @example
 * ```tsx
 * <MoonstarList empty={<MoonstarListEmpty createTo="create" hasCreate />}>
 *   // ...
 * </MoonstarList>
 * ```
 */
export const MoonstarListEmpty = (props: MoonstarListEmptyProps) => {
  const { hasCreate } = useResourceDefinition({ hasCreate: props.hasCreate });
  const resource = useResourceContext();
  const translate = useTranslate();
  const getResourceLabel = useGetResourceLabel();

  const resourceName = translate(`resources.${resource}.forcedCaseName`, {
    count: 0,
    _: getResourceLabel(resource!, 0),
  });
  const createLabel = translate('ra.action.create');

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 text-center text-muted-foreground">
      <Inbox className="size-24 opacity-50" aria-hidden />
      <p className="text-2xl font-semibold">
        {translate(`resources.${resource}.empty`, {
          _: translate('ra.page.empty', { name: resourceName }),
        })}
      </p>
      {hasCreate && (
        <>
          <p>
            {translate(`resources.${resource}.invite`, {
              _: translate('ra.page.invite'),
            })}
          </p>
          <LinkBase
            to={props.createTo}
            className={buttonVariants({ className: 'mt-4' })}
            aria-label={createLabel}
          >
            <Plus />
            {createLabel}
          </LinkBase>
        </>
      )}
    </div>
  );
};
